import type React from 'react';
import type { RefObject } from 'react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import type { InitialConfigType } from '@lexical/react/LexicalComposer';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import type { ContentEditableProps } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import type { LexicalEditor as Editor } from 'lexical';
import { ParagraphNode, TextNode } from 'lexical';

import { ActionNode } from 'components/molecules/LexicalEditor/features/action';
import { ActionHandlePlugin } from 'components/molecules/LexicalEditor/plugins/ActionHandlePlugin';
import { useToast } from 'hooks/useToast';
import * as Styled from './LexicalEditor.styled';
import EditorStatePlugin from './plugins/EditorState';
import type { Nodes } from './types';

export type { ContentEditableProps };

export type LexicalEditorProps = ContentEditableProps & {
  dataId?: string;
  children?: React.ReactNode;
  nodes?: Nodes;
  namespace?: string;
  showHandle?: boolean;
  staticHandlePosition?: boolean;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLDivElement | null>;
};

const defaultNodes: InitialConfigType['nodes'] = [
  ParagraphNode,
  TextNode,
  ActionNode,
];

export const LexicalEditor: React.FC<LexicalEditorProps> = forwardRef<
  { getEditor: () => Editor | undefined },
  LexicalEditorProps
>(
  (
    {
      children,
      nodes,
      namespace,
      readOnly,
      showHandle,
      disabled,
      dataId,
      staticHandlePosition,
      autoFocus,
      inputRef,
      height,
      placeholder,
      ...rest
    },
    ref,
  ) => {
    const toast = useToast();
    const editorRef = useRef<Editor>(undefined);

    const [floatingAnchorElem, setFloatingAnchorElem] =
      useState<HTMLDivElement | null>(null);

    const onRef = (_floatingAnchorElem: HTMLDivElement) => {
      if (_floatingAnchorElem !== null) {
        setFloatingAnchorElem(_floatingAnchorElem);
      }
    };

    const editable = !disabled && !readOnly;

    const initialConfig: InitialConfigType = {
      namespace: namespace ? namespace : `RTI_${dataId}`,
      onError: err => {
        toast.error({
          title: 'An error occurred with the rich text editor. ',
          description: err.message,
        });
      },
      nodes: [...defaultNodes, ...(nodes ? nodes : [])],
      editable,
    };

    useImperativeHandle(ref, () => {
      return {
        getEditor: () => editorRef.current,
      };
    });

    const editableId = useMemo(() => editorRef.current?.getKey(), []);

    return (
      <Styled.LexicalPocWrapper ref={inputRef} data-rti-id={dataId}>
        <LexicalComposer initialConfig={initialConfig}>
          <EditorStatePlugin editable={editable} />
          <EditorRefPlugin editorRef={editorRef} />
          <RichTextPlugin
            ErrorBoundary={LexicalErrorBoundary}
            contentEditable={
              <Styled.EditorContainer ref={onRef} style={{ minHeight: height }}>
                <Styled.Editable
                  {...(rest as any)}
                  disabled={disabled}
                  id={editableId}
                  placeholder={(isEditable: boolean) => (
                    <span id="lexical-placeholder">
                      {typeof placeholder === 'function'
                        ? placeholder(isEditable)
                        : placeholder}
                    </span>
                  )}
                />
              </Styled.EditorContainer>
            }
          />
          {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
          <HistoryPlugin />
          {floatingAnchorElem && showHandle && (
            <ActionHandlePlugin
              anchorElem={floatingAnchorElem}
              staticHandlePosition={staticHandlePosition}
            />
          )}
          {children}
        </LexicalComposer>
      </Styled.LexicalPocWrapper>
    );
  },
);
